import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '../Icon';
import { META_BUTTON_SIZE, META_BUTTON_SIZES } from './constants';

// Design System — MetaButton
// A quiet inline trigger showing one piece of metadata: an icon and a value ("3 days",
// "12.12.2024 19.00", "Visible to"). It has no frame at rest and tints on hover, so a row of
// them reads as text until pointed at. `placeholder` mutes the label when nothing is set
// yet ("Select deadline"). Purely presentational: the caller wires `onClick` or wraps it as a
// dropdown trigger; `ref` reaches the button for popover anchoring, and any other prop
// (`data-action`, `aria-*`, `type`) passes straight to the element.
// Styles: ./index.less ships via less/ats_style.less (white-label pipeline).

const MetaButton = ({
    icon,
    children,
    label,
    placeholder = false,
    active = false,
    disabled = false,
    size = META_BUTTON_SIZE.MD,
    onClick,
    className,
    ref,
    ...rest
}) => {
    const text = children ?? label;
    return (
        <button
            ref={ref}
            type="button"
            className={classNames(
                'ds-meta-button',
                `ds-meta-button--${META_BUTTON_SIZES.includes(size) ? size : META_BUTTON_SIZE.MD}`,
                {
                    'ds-meta-button--placeholder': placeholder,
                    'ds-meta-button--active': active,
                },
                className,
            )}
            disabled={disabled}
            onClick={disabled ? undefined : onClick}
            {...rest}
        >
            {icon && <Icon value={icon} noMargin className="ds-meta-button__icon" />}
            {text != null && text !== '' && <span className="ds-meta-button__label">{text}</span>}
        </button>
    );
};

MetaButton.propTypes = {
    /** Icon name (without the `icon-svg-` prefix) shown before the label */
    icon: PropTypes.string,
    /** The value shown; `label` is the same slot for callers that prefer a prop */
    children: PropTypes.node,
    label: PropTypes.node,
    /** Mutes the label: nothing is set yet and the text is a prompt */
    placeholder: PropTypes.bool,
    /** Keeps the hover tint on, e.g. while the menu it opens is showing */
    active: PropTypes.bool,
    disabled: PropTypes.bool,
    size: PropTypes.oneOf(META_BUTTON_SIZES),
    onClick: PropTypes.func,
    className: PropTypes.string,
    /** Reaches the <button>, for anchoring a popover */
    ref: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

export { META_BUTTON_SIZE, META_BUTTON_SIZES };
export default MetaButton;
